import ReportSectionCard from './shared/ReportSectionCard.component'
import ReportSectionHeading from '@components/pages/Reports/shared/ReportSectionHeading.component'
import React, { useMemo, useState } from 'react'
import ReportViewTabs from '@components/pages/Reports/shared/ReportViewTabs.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import ReportBundleStickyTopWhenExpanded from '@components/pages/Reports/shared/ReportBundleStickyTopWhenExpanded.component'
import ReportFilterToolbar from '@components/pages/Reports/shared/ReportFilterToolbar.component'
import ReportKpiGrid from '@components/pages/Reports/shared/ReportKpiGrid.component'
import ReportPageHeader from '@components/pages/Reports/shared/ReportPageHeader.component'
import { REPORTS_DISTRIBUTION_VIEW_TABS } from '@constants/Reports/patientBaseHealth.constants'
import {
  PRODUCTION_PERFORMANCE_FILTERS,
  PRODUCTION_PERFORMANCE_KPI_DEFINITIONS,
} from '@constants/Reports/productionPerformance.constants'
import {
  ReportsDistributionView,
  ReportsKpiMetric,
} from '@dataTypes/reports.type'
import { useProductionPerformanceFinance } from '@hooks/reports/useProductionPerformanceFinance'
import { useReportFinanceUiEffects } from '@hooks/reports/useReportFinanceUiEffects'
import type { ReportPeriodBucket } from '@services/api/reports/finance.api'
import {
  hasNonZeroDashboardValues,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  computeDynamicDollarAxis,
  formatKpiCurrency,
} from '@utils/reports/revenueRealizationFinance.util'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from '@components/pages/Reports/shared/ReportDesignSystemCharts.component'
import { toSentenceCase } from '@utils/textCase.util'

const REPORT_CHART_AXIS_TICK = { fontSize: 12, fill: 'var(--gray-500)' }
const REPORT_CHART_TOOLTIP_STYLE = {
  borderRadius: 8,
  border: '1px solid var(--gray-200)',
  fontSize: 12,
}
const REPORT_CHART_GRID_STROKE = 'var(--gray-100)'
const REPORT_BAR_CHART_MARGIN = { top: 24, right: 12, left: 4, bottom: 0 }
const REPORT_BAR_CATEGORY_GAP = '16%'
const REPORT_YAXIS_WIDTH = 56
const REPORT_PERCENTAGE_Y_AXIS = {
  domain: [0, 100] as [number, number],
  ticks: [0, 20, 40, 60, 80, 100],
  tickFormatter: (v: number) => `${v}%`,
}

function formatDollarLabel(value: number): string {
  if (value >= 10_000) {
    const inK = value / 1000
    const rounded = Math.round(inK)
    return `$${rounded}K`
  }
  if (value >= 1000) {
    const inK = value / 1000
    const rounded = Math.round(inK * 100) / 100
    return `$${rounded}K`
  }
  return `$${value}`
}

function formatDollarTooltip(value: number): string {
  return `$${value.toLocaleString('en-US')}`
}

function buildDollarSeries(
  rows: ReportPeriodBucket[],
  view: ReportsDistributionView,
): Array<{ month: string; barValue: number; topLabel: string }> {
  if (view === 'Count') {
    return rows.map((row) => ({
      month: row.label,
      barValue: row.value,
      topLabel: formatDollarLabel(row.value),
    }))
  }
  const total = rows.reduce((sum, r) => sum + r.value, 0) || 1
  return rows.map((row) => {
    const pct = Math.round((row.value / total) * 1000) / 10
    return {
      month: row.label,
      barValue: pct,
      topLabel: `${pct}%`,
    }
  })
}

function ProductionPerformanceReport(): React.ReactElement {
  const [productionView, setProductionView] =
    useState<ReportsDistributionView>('Count')
  const [goalView, setGoalView] = useState<ReportsDistributionView>('Count')
  const [hourView, setHourView] = useState<ReportsDistributionView>('Count')
  const [dayView, setDayView] = useState<ReportsDistributionView>('Count')
  const [reportSaveViewAction, setReportSaveViewAction] =
    useState<JSX.Element | null>(null)
  const [reportGeneratePDFAction, setReportGeneratePDFAction] =
    useState<JSX.Element | null>(null)
  // `reportFiltersAndMetrics` is the source of truth for the filters and metrics of the currently selected report view.
  // Use this for all data queries and any logic that depends on the selected report view.
  const [reportFiltersAndMetrics, setReportFiltersAndMetrics] = useState<{
    filters: Record<string, boolean>
    metrics: Record<string, boolean>
  } | null>(null)
  const { loading, error, data, bucketLabel } = useProductionPerformanceFinance(
    (reportFiltersAndMetrics?.filters ?? {}) as unknown as Record<
      string,
      string
    >,
  )
  useReportFinanceUiEffects({
    loading,
    error,
    errorMessage:
      'Failed to load production performance data. Please try again.',
  })

  const kpiItems = useMemo((): ReportsKpiMetric[] => {
    if (!data) {
      return PRODUCTION_PERFORMANCE_KPI_DEFINITIONS.map((def) => ({
        id: def.id,
        title: def.title,
        footer: def.footer,
        value: loading ? '…' : '—',
      }))
    }

    const valueById: Record<string, number> = {
      production: data.production.value,
      'production-goal': data.productionGoal.value,
      'avg-production-per-hour': data.avgProductionPerHour.value,
      'avg-production-per-day': data.avgProductionPerDay.value,
    }

    return PRODUCTION_PERFORMANCE_KPI_DEFINITIONS.map((def) => ({
      id: def.id,
      title: def.title,
      footer: def.footer,
      value: formatKpiCurrency(valueById[def.id]),
    }))
  }, [data, loading])

  const productionChartData = useMemo(
    () => buildDollarSeries(data?.production.buckets ?? [], productionView),
    [data, productionView],
  )
  const productionYAxis = useMemo(() => {
    if (productionView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicDollarAxis(
      productionChartData.map((row) => row.barValue),
    )
  }, [productionView, productionChartData])

  const goalChartData = useMemo(
    () => buildDollarSeries(data?.productionGoal.buckets ?? [], goalView),
    [data, goalView],
  )
  const goalYAxis = useMemo(() => {
    if (goalView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicDollarAxis(goalChartData.map((row) => row.barValue))
  }, [goalView, goalChartData])

  const hourChartData = useMemo(
    () => buildDollarSeries(data?.avgProductionPerHour.buckets ?? [], hourView),
    [data, hourView],
  )
  const hourYAxis = useMemo(() => {
    if (hourView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicDollarAxis(hourChartData.map((row) => row.barValue))
  }, [hourView, hourChartData])

  const dayChartData = useMemo(
    () => buildDollarSeries(data?.avgProductionPerDay.buckets ?? [], dayView),
    [data, dayView],
  )
  const dayYAxis = useMemo(() => {
    if (dayView === 'Percentage') return REPORT_PERCENTAGE_Y_AXIS
    return computeDynamicDollarAxis(dayChartData.map((row) => row.barValue))
  }, [dayView, dayChartData])

  const renderChart = (
    chartData: ReturnType<typeof buildDollarSeries>,
    axis: ReturnType<typeof computeDynamicDollarAxis>,
    view: ReportsDistributionView,
  ): React.ReactElement => (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={chartData}
        margin={REPORT_BAR_CHART_MARGIN}
        barCategoryGap={REPORT_BAR_CATEGORY_GAP}
      >
        <CartesianGrid
          vertical={false}
          strokeDasharray="0"
          stroke={REPORT_CHART_GRID_STROKE}
        />
        <XAxis
          dataKey="month"
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
        />
        <YAxis
          domain={axis.domain}
          ticks={axis.ticks}
          tickFormatter={axis.tickFormatter}
          axisLine={false}
          tickLine={false}
          tick={REPORT_CHART_AXIS_TICK}
          width={REPORT_YAXIS_WIDTH}
        />
        <Tooltip
          formatter={(value: number) =>
            view === 'Count' ? formatDollarTooltip(value) : `${value}%`
          }
          contentStyle={REPORT_CHART_TOOLTIP_STYLE}
        />
        <Bar
          dataKey="barValue"
          fill="var(--blue-400)"
          radius={[8, 8, 8, 8]}
          barSize={100}
        />
      </BarChart>
    </ResponsiveContainer>
  )

  const kpiEmpty = shouldShowDashboardSectionEmpty(
    loading,
    hasNonZeroDashboardValues(
      data?.production.value,
      data?.avgProductionPerHour.value,
      data?.avgProductionPerDay.value,
    ),
  )
  const productionEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.production?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const goalEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.productionGoal?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const hourEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.avgProductionPerHour?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )
  const dayEmpty = shouldShowDashboardSectionEmpty(
    loading,
    (data?.avgProductionPerDay?.buckets ?? []).some((row) =>
      hasNonZeroDashboardValues(row.value),
    ),
  )

  return (
    <div className="reports-patient-base-health reports-treatment-conversion-funnel reports-production-performance">
      <ReportBundleStickyTopWhenExpanded>
        <ReportPageHeader title="Production Performance">
          {reportSaveViewAction}
          {reportGeneratePDFAction}
        </ReportPageHeader>

        <ReportFilterToolbar
          filters={PRODUCTION_PERFORMANCE_FILTERS}
          onReportSaveViewActionChange={setReportSaveViewAction}
          onReportGeneratePDFActionChange={setReportGeneratePDFAction}
          onReportFiltersAndMetricsChange={setReportFiltersAndMetrics}
        />
      </ReportBundleStickyTopWhenExpanded>

      {kpiEmpty ? (
        <DashboardSectionEmptyState />
      ) : (
        <ReportKpiGrid items={kpiItems} />
      )}

      <div className="reports-treatment-conversion-funnel__stack">
        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Production ({bucketLabel})
                </ReportSectionHeading>
                {productionEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={productionView}
                    onChange={(value) =>
                      setProductionView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {productionEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderChart(
                    productionChartData,
                    productionYAxis,
                    productionView,
                  )}
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  {toSentenceCase('Production Goal vs Actual')} ({bucketLabel})
                </ReportSectionHeading>
                {goalEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={goalView}
                    onChange={(value) =>
                      setGoalView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {goalEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderChart(goalChartData, goalYAxis, goalView)}
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>

        <div className="reports-treatment-conversion-funnel__grid">
          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Average Production per Hour ({bucketLabel})
                </ReportSectionHeading>
                {hourEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={hourView}
                    onChange={(value) =>
                      setHourView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {hourEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderChart(hourChartData, hourYAxis, hourView)}
                </div>
              )}
            </div>
          </ReportSectionCard>

          <ReportSectionCard className="reports-patient-base-health__chart-card">
            <div className="reports-patient-value-revenue__column">
              <div className="reports-patient-base-health__chart-head">
                <ReportSectionHeading>
                  Average Production per Day ({bucketLabel})
                </ReportSectionHeading>
                {dayEmpty ? null : (
                  <ReportViewTabs
                    options={REPORTS_DISTRIBUTION_VIEW_TABS}
                    value={dayView}
                    onChange={(value) =>
                      setDayView(value as ReportsDistributionView)
                    }
                    className="reports-patient-base-health__view-group"
                  />
                )}
              </div>
              {dayEmpty ? (
                <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
              ) : (
                <div className="reports-treatment-conversion-funnel__chart">
                  {renderChart(dayChartData, dayYAxis, dayView)}
                </div>
              )}
            </div>
          </ReportSectionCard>
        </div>
      </div>
    </div>
  )
}

export default ProductionPerformanceReport
